import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  BroadcastMessage,
  BroadcastMessageCreate,
  RecipientFilter,
} from '../types/notification-admin';
import { NotificationService } from './notificationService';
import { UserRole } from '../types/auth';
import { NotificationType } from '../types/user';

/**
 * NotificationBroadcastService - Manages bulk notification sending and broadcasting
 *
 * Features:
 * - Create and manage broadcast messages
 * - User selection with advanced filtering
 * - Scheduling broadcasts for future delivery
 * - Track broadcast history and status
 * - Support for different recipient filters
 */
class NotificationBroadcastServiceImpl {
  private readonly COLLECTION_NAME = 'broadcastMessages';
  private readonly USERS_COLLECTION = 'users';

  // ==================== BROADCAST MANAGEMENT ====================

  /**
   * Create a new broadcast draft
   *
   * @param data Broadcast message data
   * @param createdBy Admin user ID
   * @returns Created broadcast message
   */
  async createBroadcast(
    data: BroadcastMessageCreate,
    createdBy: string
  ): Promise<BroadcastMessage> {
    try {
      // Get recipient count and IDs
      const recipientIds = await this.getRecipientUserIds(data.recipientFilter);
      const recipientCount = recipientIds.length;

      // Build broadcast data, excluding undefined fields
      const broadcastData: any = {
        title: data.title,
        message: data.message,
        priority: data.priority,
        recipientFilter: data.recipientFilter,
        recipientCount,
        recipientIds,
        scheduledFor: data.scheduledFor ? Timestamp.fromDate(data.scheduledFor) : null,
        status: data.scheduledFor ? 'scheduled' : 'draft',
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Only add optional fields if they have values
      if (data.actionLabel) {
        broadcastData.actionLabel = data.actionLabel;
      }
      if (data.actionUrl) {
        broadcastData.actionUrl = data.actionUrl;
      }

      const docRef = await addDoc(
        collection(db, this.COLLECTION_NAME),
        broadcastData
      );

      return {
        id: docRef.id,
        ...data,
        recipientCount,
        recipientIds,
        status: data.scheduledFor ? 'scheduled' : 'draft',
        createdBy,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as BroadcastMessage;
    } catch (error: any) {
      console.error('Error creating broadcast:', error);
      throw new Error(`Failed to create broadcast: ${error.message}`);
    }
  }

  /**
   * Update an existing broadcast
   *
   * @param broadcastId Broadcast ID
   * @param updates Partial broadcast updates
   */
  async updateBroadcast(
    broadcastId: string,
    updates: Partial<BroadcastMessageCreate>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, broadcastId);
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Recalculate recipient count if filter changed
      if (updates.recipientFilter) {
        const recipientIds = await this.getRecipientUserIds(updates.recipientFilter);
        updateData.recipientCount = recipientIds.length;
        updateData.recipientIds = recipientIds;
      }

      // Convert date to Timestamp if needed
      if (updates.scheduledFor) {
        updateData.scheduledFor = Timestamp.fromDate(updates.scheduledFor);
        updateData.status = 'scheduled';
      }

      await updateDoc(docRef, updateData);
    } catch (error: any) {
      console.error('Error updating broadcast:', error);
      throw new Error(`Failed to update broadcast: ${error.message}`);
    }
  }

  /**
   * Get broadcast by ID
   *
   * @param broadcastId Broadcast ID
   * @returns Broadcast message or null
   */
  async getBroadcastById(broadcastId: string): Promise<BroadcastMessage | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, broadcastId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.mapDocumentToBroadcast(docSnap);
    } catch (error: any) {
      console.error('Error getting broadcast:', error);
      return null;
    }
  }

  /**
   * Get recipient count for a filter
   *
   * @param filter Recipient filter
   * @returns Number of recipients
   */
  async getRecipientCount(filter: RecipientFilter): Promise<number> {
    try {
      const userIds = await this.getRecipientUserIds(filter);
      return userIds.length;
    } catch (error: any) {
      console.error('Error getting recipient count:', error);
      return 0;
    }
  }

  /**
   * Get recipient user IDs based on filter
   *
   * @param filter Recipient filter
   * @returns Array of user IDs
   */
  async getRecipientUserIds(filter: RecipientFilter): Promise<string[]> {
    try {
      // If custom user IDs specified, use them directly
      if (filter.customUserIds && filter.customUserIds.length > 0) {
        return filter.customUserIds;
      }

      // Build query based on filters
      const constraints: any[] = [];

      // Filter by role
      if (filter.roles && filter.roles.length > 0) {
        constraints.push(where('role', 'in', filter.roles));
      }

      // Filter by activity status
      if (filter.activityStatus) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        if (filter.activityStatus === 'active') {
          constraints.push(where('lastLoginAt', '>=', Timestamp.fromDate(thirtyDaysAgo)));
        } else {
          constraints.push(where('lastLoginAt', '<', Timestamp.fromDate(thirtyDaysAgo)));
        }
      }

      // Filter by registration date range
      if (filter.registrationDateRange) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(filter.registrationDateRange.start))
        );
        constraints.push(
          where('createdAt', '<=', Timestamp.fromDate(filter.registrationDateRange.end))
        );
      }

      // If no specific filters and allUsers is true, get all users
      let q;
      if (constraints.length === 0 && filter.allUsers) {
        q = query(collection(db, this.USERS_COLLECTION));
      } else if (constraints.length > 0) {
        q = query(collection(db, this.USERS_COLLECTION), ...constraints);
      } else {
        // No valid filter
        return [];
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.id);
    } catch (error: any) {
      console.error('Error getting recipient user IDs:', error);
      return [];
    }
  }

  /**
   * Send broadcast immediately
   *
   * @param broadcastId Broadcast ID
   */
  async sendBroadcast(broadcastId: string): Promise<void> {
    try {
      const broadcast = await this.getBroadcastById(broadcastId);
      if (!broadcast) {
        throw new Error('Broadcast not found');
      }

      // Check if already sent
      if (broadcast.status === 'sent') {
        throw new Error('Broadcast already sent');
      }

      // Update status to sending
      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        status: 'sending',
        updatedAt: serverTimestamp(),
      });

      // Get recipient IDs
      const recipientIds = broadcast.recipientIds ||
        await this.getRecipientUserIds(broadcast.recipientFilter);

      // Send notifications to all recipients
      await NotificationService.createBulkNotification(recipientIds, {
        type: 'admin_broadcast' as NotificationType,
        title: broadcast.title,
        message: broadcast.message,
        priority: broadcast.priority,
        actionLabel: broadcast.actionLabel,
        actionUrl: broadcast.actionUrl,
        data: {
          broadcastId: broadcast.id,
        },
      });

      // Update status to sent
      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        status: 'sent',
        sentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error sending broadcast:', error);

      // Update status to failed
      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        status: 'failed',
        errorMessage: error.message,
        updatedAt: serverTimestamp(),
      });

      throw new Error(`Failed to send broadcast: ${error.message}`);
    }
  }

  /**
   * Schedule a broadcast for future delivery
   *
   * @param broadcastId Broadcast ID
   * @param scheduledFor Date/time to send
   */
  async scheduleBroadcast(broadcastId: string, scheduledFor: Date): Promise<void> {
    try {
      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        scheduledFor: Timestamp.fromDate(scheduledFor),
        status: 'scheduled',
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error scheduling broadcast:', error);
      throw new Error(`Failed to schedule broadcast: ${error.message}`);
    }
  }

  /**
   * Cancel a scheduled broadcast
   *
   * @param broadcastId Broadcast ID
   */
  async cancelBroadcast(broadcastId: string): Promise<void> {
    try {
      const broadcast = await this.getBroadcastById(broadcastId);
      if (!broadcast) {
        throw new Error('Broadcast not found');
      }

      if (broadcast.status !== 'scheduled' && broadcast.status !== 'draft') {
        throw new Error('Can only cancel scheduled or draft broadcasts');
      }

      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error cancelling broadcast:', error);
      throw new Error(`Failed to cancel broadcast: ${error.message}`);
    }
  }

  /**
   * Delete a broadcast
   *
   * @param broadcastId Broadcast ID
   */
  async deleteBroadcast(broadcastId: string): Promise<void> {
    try {
      const broadcast = await this.getBroadcastById(broadcastId);
      if (!broadcast) {
        throw new Error('Broadcast not found');
      }

      // Only allow deletion of drafts, cancelled, or failed broadcasts
      if (!['draft', 'cancelled', 'failed'].includes(broadcast.status)) {
        throw new Error('Can only delete draft, cancelled, or failed broadcasts');
      }

      await updateDoc(doc(db, this.COLLECTION_NAME, broadcastId), {
        status: 'cancelled',
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error deleting broadcast:', error);
      throw new Error(`Failed to delete broadcast: ${error.message}`);
    }
  }

  /**
   * Get broadcast history
   *
   * @param limit Maximum number of broadcasts to return
   * @returns Array of broadcast messages
   */
  async getBroadcastHistory(limit: number = 50): Promise<BroadcastMessage[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limit)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocumentToBroadcast(doc));
    } catch (error: any) {
      console.error('Error getting broadcast history:', error);
      return [];
    }
  }

  /**
   * Get scheduled broadcasts
   *
   * @returns Array of scheduled broadcast messages
   */
  async getScheduledBroadcasts(): Promise<BroadcastMessage[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', '==', 'scheduled'),
        orderBy('scheduledFor', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocumentToBroadcast(doc));
    } catch (error: any) {
      console.error('Error getting scheduled broadcasts:', error);
      return [];
    }
  }

  /**
   * Get draft broadcasts
   *
   * @returns Array of draft broadcast messages
   */
  async getDraftBroadcasts(): Promise<BroadcastMessage[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', '==', 'draft'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocumentToBroadcast(doc));
    } catch (error: any) {
      console.error('Error getting draft broadcasts:', error);
      return [];
    }
  }

  // ==================== HELPERS ====================

  /**
   * Map Firestore document to BroadcastMessage
   */
  private mapDocumentToBroadcast(doc: any): BroadcastMessage {
    const data = doc.data();
    return {
      id: doc.id,
      title: data.title,
      message: data.message,
      priority: data.priority,
      actionLabel: data.actionLabel,
      actionUrl: data.actionUrl,
      recipientFilter: data.recipientFilter,
      recipientCount: data.recipientCount,
      recipientIds: data.recipientIds,
      scheduledFor: data.scheduledFor?.toDate(),
      sentAt: data.sentAt?.toDate(),
      status: data.status,
      createdBy: data.createdBy,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      errorMessage: data.errorMessage,
    };
  }
}

// Export singleton instance
export const NotificationBroadcastService = new NotificationBroadcastServiceImpl();
